import { useEffect, useRef, useState } from "react";
import { MessageCircle, Loader2, Link2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";

const digits = (p) => {
  let d = String(p || "").replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d.length === 10 ? `91${d}` : d;
};
export const waUrl = (phone, text) => `https://wa.me/${digits(phone)}?text=${encodeURIComponent(text)}`;
const shareUrl = async (id) => `${process.env.REACT_APP_BACKEND_URL}/api/public/files/${(await api.post(`/files/${id}/share`)).data.token}`;

const SIGN = "\n\nRegards,\nZero Design Studio";
const hi = (p) => (p.client ? `Hello ${p.client},` : "Hello,");
const at = (p) => (p.venue && !/tbc|to be confirmed/i.test(p.venue) ? ` at ${p.venue}` : "");
export const TEMPLATES = [
  ["render", "Render shared", (p, t) => `${hi(p)}\n\nSharing the latest renders${t ? ` (${t})` : ""} for ${p.name || "your event"}. Please have a look and share your feedback or approval.${SIGN}`],
  ["reminder", "Approval reminder", (p, t) => `${hi(p)}\n\nGentle reminder: we are awaiting your approval${t ? ` on "${t}"` : ""} for ${p.name || "your event"}.${p.client_deadline ? ` We need your confirmation by ${p.client_deadline}` : " An early confirmation"} to keep production on schedule.${SIGN}`],
  ["eventday", "Event-day update", (p) => `${hi(p)}\n\nEvent-day update for ${p.name || "your event"}${at(p)}: the team is on site and setup is progressing on schedule. We will share photos and keep you posted on every milestone.${SIGN}`],
];

function TemplateChips({ project, title, original, onPick, active }) {
  const chips = [["original", "Default", () => original], ...TEMPLATES];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="whatsapp-templates">
      {chips.map(([key, label, fn]) => (
        <button type="button" key={key} data-testid={`whatsapp-template-${key}`} onClick={() => onPick(key, fn(project, title))}
          className={`rounded-full border px-3 py-1 text-xs transition-colors ${active === key ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-600 hover:border-violet-300 hover:text-zinc-900"}`}>{label}</button>
      ))}
    </div>
  );
}

export function WhatsAppDialog({ open, onClose, phone = "", text = "", entityId, fileIds = [], attachment, projectId, approvalId, context, onSent, title }) {
  const [project, setProject] = useState({});
  const [tpl, setTpl] = useState("original");
  const [links, setLinks] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [num, setNum] = useState(phone);
  const [msg, setMsg] = useState(text);
  const [files, setFiles] = useState([]);
  const [picked, setPicked] = useState(fileIds);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setNum(phone); setMsg(text); setTpl("original"); setPicked(attachment ? [attachment.id] : fileIds); setLinks([]); setLinkUrl("");
    setProject({});
    if (projectId) api.get(`/projects/${projectId}`).then((r) => { setProject(r.data); if (!phone && r.data.client_whatsapp) setNum(r.data.client_whatsapp); }).catch(() => {});
    if (attachment) setFiles([{ id: attachment.id, original_filename: attachment.name }]);
    else if (entityId) api.get("/files", { params: { entity_id: entityId } }).then((r) => setFiles(r.data)).catch(() => setFiles([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const valid = digits(num).length >= 11 && digits(num).length <= 15;
  const addLink = () => {
    let u = linkUrl.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
    if (!/^https?:\/\/[^\s/]+\.[^\s/]{2,}(\/\S*)?$/i.test(u)) { toast.error("Enter a valid link, e.g. https://drive.google.com/..."); return; }
    setLinks((l) => [...l, u]); setLinkUrl("");
  };
  const upload = async (ev) => {
    const chosen = [...ev.target.files];
    ev.target.value = "";
    if (!chosen.length) return;
    setUploading(true);
    for (const f of chosen) {
      const fd = new FormData();
      fd.append("file", f); fd.append("entity_type", "whatsapp"); fd.append("entity_id", projectId || "general"); fd.append("category", "whatsapp");
      try {
        const { data } = await api.post("/files", fd);
        setFiles((x) => [...x, data]); setPicked((p) => [...p, data.id]);
      } catch (err) { toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail) || "upload failed"}`); }
    }
    setUploading(false);
  };
  const send = async () => {
    setBusy(true);
    try {
      const fileLinks = await Promise.all(picked.map(async (id) => `${files.find((f) => f.id === id)?.original_filename || "File"}: ${await shareUrl(id)}`));
      const all = [...fileLinks, ...links];
      const full = all.length ? `${msg}\n\n${all.join("\n")}` : msg;
      window.open(waUrl(num, full), "_blank", "noopener");
      api.post("/whatsapp/log", { phone: `+${digits(num)}`, text: full, project_id: projectId || null, approval_id: approvalId || null, context: tpl !== "original" ? `${TEMPLATES.find(([k]) => k === tpl)[1]}${context ? ` · ${context}` : ""}` : context || null }).then(() => onSent?.()).catch(() => {});
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not prepare the WhatsApp message"); }
    setBusy(false);
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="whatsapp-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-emerald-600" />Send to client on WhatsApp</DialogTitle>
          <DialogDescription>Opens WhatsApp Web or the app with this message ready. Press Send there.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-zinc-600">Client WhatsApp number
            <input data-testid="whatsapp-phone-input" value={num} onChange={(e) => setNum(e.target.value)} placeholder="+91 98xxxxxxxx"
              className="mt-1 h-10 w-full rounded-lg border border-zinc-200 px-3 text-sm font-normal focus:border-violet-300 focus:outline-none" />
            {num && !valid && <span className="mt-1 block font-normal text-rose-600">Enter the number with country code, e.g. +91 98765 43210</span>}
          </label>
          <div className="space-y-1.5"><div className="text-xs font-semibold text-zinc-600">Ready-made messages</div>
            <TemplateChips project={project} title={title} original={text} active={tpl} onPick={(k, t) => { setTpl(k); setMsg(t); }} />
          </div>
          <label className="block text-xs font-semibold text-zinc-600">Message
            <textarea data-testid="whatsapp-message-input" rows={6} value={msg} onChange={(e) => setMsg(e.target.value)}
              className="mt-1 w-full rounded-lg border border-zinc-200 p-3 text-sm font-normal focus:border-violet-300 focus:outline-none" />
          </label>
          {files.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-zinc-600">Attach file links <span className="font-normal text-zinc-400">(client can open them for 30 days, no login needed)</span></div>
              <div className="mt-1 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-zinc-100 p-2">
                {files.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 text-sm text-zinc-700">
                    <input type="checkbox" data-testid={`whatsapp-file-${f.id}`} checked={picked.includes(f.id)} onChange={() => toggle(f.id)} />
                    <span className="truncate">{f.original_filename}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2 rounded-lg border border-dashed border-zinc-200 p-2.5">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link2 className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                <input data-testid="whatsapp-link-input" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }}
                  placeholder="Paste a link (Drive, Dropbox, website…)" className="h-9 w-full rounded-lg border border-zinc-200 pl-8 pr-2 text-sm focus:border-violet-300 focus:outline-none" />
              </div>
              <Button type="button" data-testid="whatsapp-link-add" size="sm" variant="outline" className="h-9" onClick={addLink}>Add link</Button>
              <input ref={fileRef} type="file" multiple accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.dwg,.csv,.txt" className="hidden" data-testid="whatsapp-file-upload-input" onChange={upload} />
              <Button type="button" data-testid="whatsapp-file-upload" size="sm" variant="outline" className="h-9" disabled={uploading} onClick={() => fileRef.current.click()}>
                {uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Paperclip className="mr-1 h-3.5 w-3.5" />}{uploading ? "Uploading…" : "Upload file"}
              </Button>
            </div>
            {links.map((u, i) => (
              <div key={u + i} data-testid={`whatsapp-link-${i}`} className="flex items-center gap-2 rounded-md bg-zinc-50 px-2 py-1 text-xs text-zinc-700">
                <Link2 className="h-3 w-3 shrink-0 text-violet-600" /><span className="min-w-0 flex-1 truncate">{u}</span>
                <button type="button" data-testid={`whatsapp-link-remove-${i}`} onClick={() => setLinks((l) => l.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <div className="text-[10px] text-zinc-400">PDFs, images and other files are uploaded and sent as a secure link the client can open for 30 days.</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="whatsapp-send-button" className="bg-emerald-600 hover:bg-emerald-700" disabled={!valid || !msg.trim() || busy} onClick={send}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-1 h-4 w-4" />}Open WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WhatsAppHistory({ projectId, refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    api.get("/whatsapp/log", { params: { project_id: projectId } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [projectId, refreshKey]);
  return (
    <div data-testid="whatsapp-history" className="rounded-lg border border-zinc-100 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500"><MessageCircle className="h-3.5 w-3.5 text-emerald-600" />WhatsApp history {rows?.length ? `(${rows.length})` : ""}</div>
      {rows === null ? <div className="text-xs text-zinc-400">Loading…</div> : !rows.length ? <div className="text-xs text-zinc-400">No WhatsApp messages sent to the client yet.</div> : (
        <div className="thin-scroll max-h-72 space-y-2 overflow-y-auto">
          {rows.map((r) => (
            <button type="button" key={r.id} data-testid={`whatsapp-history-${r.id}`} onClick={() => setOpenId(openId === r.id ? null : r.id)} className={`block w-full rounded-md p-2.5 text-left transition-colors ${r.direction === "in" ? "ml-auto bg-violet-50/70 hover:bg-violet-50" : "bg-emerald-50/60 hover:bg-emerald-50"}`}>
              <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] text-zinc-500">
                <span><b className="text-zinc-800">{r.context || "Message"}</b> · {r.direction === "in" ? `from ${r.phone} · logged` : `to ${r.phone}`}</span>
                <span>{r.created_at.slice(0, 16).replace("T", " ")} · {r.sent_by}</span>
              </div>
              <div className={`mt-1 whitespace-pre-wrap break-words text-xs text-zinc-700 ${openId === r.id ? "" : "line-clamp-2"}`}>{r.text}</div>
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 text-[10px] text-zinc-400">Logged when “Open WhatsApp” is clicked — the message is delivered once it is sent in WhatsApp.</div>
    </div>
  );
}

export function WhatsAppButton({ testid, label = "WhatsApp client", size = "sm", variant = "outline", iconOnly = false, ...props }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" data-testid={testid} size={size} variant={variant} title="Send to client on WhatsApp"
        className={iconOnly ? "h-6 w-6 p-0 text-emerald-600 hover:text-emerald-700" : "text-emerald-700"} onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        <MessageCircle className={iconOnly ? "h-3.5 w-3.5" : "mr-1 h-3.5 w-3.5"} />{!iconOnly && label}
      </Button>
      <WhatsAppDialog open={open} onClose={() => setOpen(false)} {...props} />
    </>
  );
}
